/**
 * MH-08 Điều khiển thiết bị — NGĂN TRƯỢT BÊN, không phải trang riêng, để người
 * dùng không mất ngữ cảnh danh sách (mục 4.7 đặc tả giao diện).
 *
 * Dùng chung cho một thiết bị và cho nhiều thiết bị đã chọn. Khi nhiều thiết bị có
 * giá trị hiện tại khác nhau, ngăn hiển thị "nhiều giá trị" chứ KHÔNG lấy giá trị
 * của thiết bị đầu tiên — lấy giá trị đầu tiên sẽ khiến người dùng tưởng cả lô
 * đang ở mức đó rồi bấm gửi mà không sửa.
 *
 * Sau khi gửi, ngăn KHÔNG đóng ngay mà chuyển sang hiển thị tiến trình theo từng
 * thiết bị với ba nhóm thành công, đang chờ, lỗi.
 */
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { dienVao, vi } from '../ngon-ngu/vi';
import { thietBiApi, LoiApi, type DongThietBi, type KetQuaHangLoat } from '../api/thiet-bi-api';
import { HopXacNhan } from '../thanh-phan/HopXacNhan';
import { BangKetQuaHangLoat } from '../thanh-phan/KetQuaHangLoat';

/** Trên ngưỡng này, nâng cấp firmware là một ĐỢT cần theo dõi (MH-09). */
export const NGUONG_CHUYEN_MH09 = 50;

export interface NganDieuKhienProps {
  thietBi: DongThietBi[];
  onDong: () => void;
  /** Ghi đè lời gọi API, dùng cho kiểm thử thành phần. */
  guiLenh?: typeof thietBiApi.guiLenh;
}

type TenLenh = 'amLuong' | 'congSuat' | 'dungPhat' | 'khoiDongLai' | 'nangCap';

/** Giá trị hiện tại dùng chung; `null` nghĩa là các thiết bị khác nhau giá trị. */
function giaTriChung<T>(ds: (T | null)[]): T | null {
  const dau = ds[0] ?? null;
  return ds.every((x) => x === dau) ? dau : null;
}

export function MH08NganDieuKhien({ thietBi, onDong, guiLenh }: NganDieuKhienProps): ReactNode {
  const goi = guiLenh ?? thietBiApi.guiLenh;
  const [lenh, datLenh] = useState<TenLenh>('amLuong');
  const [amLuong, datAmLuong] = useState<number>(() => giaTriChung(thietBi.map((t) => t.amLuong)) ?? 50);
  const [congSuat, datCongSuat] = useState<string>('1');
  const [dungPhat, datDungPhat] = useState<string>('0');
  const [treGiay, datTreGiay] = useState<string>('0');
  const [hoiXacNhan, datHoiXacNhan] = useState(false);
  const [dangGui, datDangGui] = useState(false);
  const [ketQua, datKetQua] = useState<KetQuaHangLoat | null>(null);
  const [loiGui, datLoiGui] = useState<string | null>(null);
  const ngan = useRef<HTMLDivElement | null>(null);

  const soGianTiep = thietBi.filter((t) => t.phuongThucKetNoi === 'gian_tiep').length;
  const amLuongChung = useMemo(() => giaTriChung(thietBi.map((t) => t.amLuong)), [thietBi]);
  const congSuatChung = useMemo(() => giaTriChung(thietBi.map((t) => t.congSuat)), [thietBi]);

  useEffect(() => {
    // Ngăn trượt mở ra thì đưa tiêu điểm vào ngăn, và Esc thì đóng.
    ngan.current?.focus();
    const esc = (e: KeyboardEvent): void => {
      if (e.key === 'Escape' && !hoiXacNhan) onDong();
    };
    document.addEventListener('keydown', esc);
    return () => document.removeEventListener('keydown', esc);
  }, [onDong, hoiXacNhan]);

  const thamSo = (): string => {
    if (lenh === 'amLuong') return String(amLuong);
    if (lenh === 'congSuat') return congSuat;
    if (lenh === 'dungPhat') return dungPhat;
    if (lenh === 'khoiDongLai') return treGiay || '0';
    return '';
  };

  const maLenh = (): string =>
    lenh === 'amLuong' ? '0' : lenh === 'congSuat' ? '1' : lenh === 'dungPhat' ? '2' : '3';

  /** Hệ quả định lượng cho hộp xác nhận: luôn nêu SỐ thiết bị bị ảnh hưởng. */
  const heQuaXacNhan = (): string => {
    const so = thietBi.length;
    if (lenh === 'khoiDongLai') return dienVao(vi.dieuKhien.xacNhanKhoiDongLai, { so });
    if (lenh === 'amLuong') return dienVao(vi.dieuKhien.xacNhanAmLuong, { so, thamSo: amLuong });
    if (lenh === 'congSuat') {
      return dienVao(vi.dieuKhien.xacNhanCongSuat, {
        so,
        thamSo: congSuat === '1' ? vi.chiTiet.bat : vi.chiTiet.tat,
      });
    }
    return dienVao(vi.dieuKhien.xacNhanDungPhat, { so });
  };

  const gui = async (ids: string[]): Promise<void> => {
    datDangGui(true);
    datLoiGui(null);
    try {
      const kq = await goi({ maLenh: maLenh(), thamSo: thamSo(), deviceIds: ids });
      // Không lỗi thì đóng ngăn luôn, không hiện bảng kết quả (yêu cầu của khách).
      if (kq.loi.length === 0) {
        onDong();
        return;
      }
      datKetQua(kq);
    } catch (e) {
      datLoiGui(e instanceof LoiApi ? e.message : vi.chung.loi);
    } finally {
      datDangGui(false);
      datHoiXacNhan(false);
    }
  };

  return (
    <aside
      className="ngan-truot"
      data-vung="ngan-dieu-khien"
      /* Ngăn trượt bên KHÔNG phải hộp thoại chặn: người dùng vẫn phải thấy và
         dùng được danh sách phía sau, nên là vùng có nhãn, không phải dialog. */
      role="region"
      aria-label={vi.dieuKhien.tieuDe}
      tabIndex={-1}
      ref={ngan}
    >
      <header className="ngan-truot__dau">
        <h2>{vi.dieuKhien.tieuDe}</h2>
        <p>{dienVao(vi.dieuKhien.soThietBi, { so: thietBi.length })}</p>
        <button type="button" className="nut nut--nho" onClick={onDong}>
          {vi.dieuKhien.dong}
        </button>
      </header>

      {/* Thiết bị kết nối gián tiếp: nêu rõ lệnh đi qua service nhà cung cấp nên
          thời gian phản hồi phụ thuộc cam kết của nhà cung cấp đó. */}
      {soGianTiep > 0 ? (
        <p className="dai-thong-bao" data-vung="ghi-chu-gian-tiep" role="note">
          {dienVao(vi.dieuKhien.ghiChuGianTiep, { so: soGianTiep })}
        </p>
      ) : null}

      {ketQua ? (
        <BangKetQuaHangLoat
          ketQua={ketQua}
          dangThuLai={dangGui}
          onThuLaiPhanLoi={(ids) => void gui(ids)}
          onDong={onDong}
        />
      ) : (
        <div className="ngan-truot__than">
          <fieldset>
            <legend>{vi.dieuKhien.tieuDe}</legend>
            {(
              [
                ['amLuong', vi.dieuKhien.lenh.amLuong],
                ['congSuat', vi.dieuKhien.lenh.congSuat],
                ['dungPhat', vi.dieuKhien.lenh.dungPhat],
                ['khoiDongLai', vi.dieuKhien.lenh.khoiDongLai],
                ['nangCap', vi.dieuKhien.lenh.nangCap],
              ] as [TenLenh, string][]
            ).map(([ma, nhan]) => (
              <label key={ma} className="ngan-truot__chon-lenh">
                <input
                  type="radio"
                  name="lenh"
                  data-chon-lenh={ma}
                  value={ma}
                  checked={lenh === ma}
                  onChange={() => datLenh(ma)}
                />
                {nhan}
              </label>
            ))}
          </fieldset>

          {lenh === 'amLuong' ? (
            <p className="truong">
              <label htmlFor="dk-am-luong">{vi.dieuKhien.lenh.amLuong}</label>
              {amLuongChung === null ? (
                <span className="truong__nhieu-gia-tri">{vi.dieuKhien.nhieuGiaTri}</span>
              ) : null}
              {/* Chỉ gửi khi nhả chuột hoặc rời ô số, không gửi theo từng bước
                  trượt; bước nhảy 5 đơn vị khi dùng bàn phím (Bảng 4.4). */}
              <input
                id="dk-am-luong"
                type="range"
                min={0}
                max={100}
                step={5}
                value={amLuong}
                onChange={(e) => datAmLuong(Number(e.target.value))}
              />
              <input
                type="number"
                min={0}
                max={100}
                value={amLuong}
                aria-label={vi.dieuKhien.lenh.amLuong}
                onChange={(e) => datAmLuong(Number(e.target.value))}
              />
            </p>
          ) : null}

          {lenh === 'congSuat' ? (
            <p className="truong">
              <label htmlFor="dk-cong-suat">{vi.dieuKhien.lenh.congSuat}</label>
              {congSuatChung === null ? (
                <span className="truong__nhieu-gia-tri">{vi.dieuKhien.nhieuGiaTri}</span>
              ) : null}
              <select id="dk-cong-suat" value={congSuat} onChange={(e) => datCongSuat(e.target.value)}>
                <option value="1">{vi.chiTiet.bat}</option>
                <option value="0">{vi.chiTiet.tat}</option>
              </select>
              <span className="truong__mo-ta">{vi.dieuKhien.moTaCongSuat}</span>
            </p>
          ) : null}

          {lenh === 'dungPhat' ? (
            <p className="truong">
              <label htmlFor="dk-dung-phat">{vi.dieuKhien.lenh.dungPhat}</label>
              {/* Bốn giá trị đúng theo mã lệnh của dịch vụ điều khiển, không gộp
                  và không đổi thứ tự. */}
              <select
                id="dk-dung-phat"
                data-o="dung-phat"
                value={dungPhat}
                onChange={(e) => datDungPhat(e.target.value)}
              >
                <option value="0">{vi.dieuKhien.dungPhatLuaChon.tatCa}</option>
                <option value="1">{vi.dieuKhien.dungPhatLuaChon.hienThoi}</option>
                <option value="2">{vi.dieuKhien.dungPhatLuaChon.tamDung}</option>
                <option value="3">{vi.dieuKhien.dungPhatLuaChon.phatTiep}</option>
              </select>
            </p>
          ) : null}

          {lenh === 'khoiDongLai' ? (
            <p className="truong">
              <label htmlFor="dk-tre">{vi.dieuKhien.khoiDongLaiTre}</label>
              <input
                id="dk-tre"
                type="number"
                min={0}
                max={3600}
                value={treGiay}
                onChange={(e) => datTreGiay(e.target.value)}
              />
            </p>
          ) : null}

          {lenh === 'nangCap' ? (
            <p className="dai-thong-bao" role="note">
              {thietBi.length > NGUONG_CHUYEN_MH09
                ? vi.dieuKhien.nangCapChuyenMH09
                : vi.thietBi.hangLoat.chuaLam}
            </p>
          ) : null}

          {loiGui ? (
            <p className="dai-thong-bao dai-thong-bao--loi" role="alert">
              {loiGui}
            </p>
          ) : null}

          <button
            type="button"
            className="nut nut--chinh"
            data-hanh-dong="gui-lenh"
            disabled={dangGui || lenh === 'nangCap'}
            onClick={() => datHoiXacNhan(true)}
          >
            {dangGui ? vi.dieuKhien.dangGui : vi.dieuKhien.gui}
          </button>
        </div>
      )}

      {hoiXacNhan ? (
        <HopXacNhan
          tieuDe={vi.dieuKhien.xacNhanTieuDe}
          heQua={heQuaXacNhan()}
          ghiChu={soGianTiep > 0 ? dienVao(vi.dieuKhien.ghiChuGianTiep, { so: soGianTiep }) : undefined}
          nhanThucHien={vi.dieuKhien.gui}
          onHuy={() => datHoiXacNhan(false)}
          onThucHien={() => void gui(thietBi.map((t) => t.id))}
        />
      ) : null}

      <p className="ngan-truot__ghi-chu">{vi.dieuKhien.mocGhiChu}</p>
    </aside>
  );
}
